/**
 * Maps custom export "Who can access" TreeSelect values to and from
 * `access`: { userIds, positionIds } on create/update payloads. `userIds` are
 * plain dashboard user id strings on write. Fetched rows may include
 * `{ userId, isPermanent }` entries; read accepts plain strings or objects.
 * Position ids are company roles (same id shape as workflow assignees).
 */

/**
 * User ids marked `isPermanent` in `access.userIds` (cannot be removed in UI).
 * @param {object} exportRow
 * @returns {string[]}
 */
export const getPermanentUserIdsFromAccessPayload = (exportRow = {}) => {
  const { userIds = [] } = exportRow?.access ?? {};

  return userIds
    .filter((row) => row.userId && row.isPermanent)
    .map((row) => row.userId);
};

/**
 * @param {string[]} selectedIds TreeSelect values (positions + users).
 * @param {Set<string>} positionIdSet
 * @param {Set<string>} userIdSet
 * @returns {{ userIds: string[], positionIds: string[] }}
 */
export const getAccessPayloadFromPermissionIds = (selectedIds, {
  positionIdSet,
  userIdSet,
}) => {
  const userIds = [];
  const positionIds = [];

  selectedIds.forEach((rawId) => {
    const id = typeof rawId === 'string' ? rawId.trim() : rawId;
    if (!id) return;

    if (positionIdSet.has(id)) {
      positionIds.push(id);
      return;
    }
    if (userIdSet.has(id)) {
      userIds.push(id);
    }
  });
  return { userIds, positionIds };
};

/**
 * Inverse of {@link getAccessPayloadFromPermissionIds} for editing —
 * access payload → permission ids.
 * @param {object} exportRow
 * @param {{
 *   userIds?: Array<{ userId: string, isPermanent: boolean } | string>,
 *   positionIds?: string[],
 * }} [exportRow.access]
 * @returns {string[]}
 */
export const getPermissionIdsFromAccessPayload = (exportRow = {}) => {
  const { userIds = [], positionIds = [] } = exportRow?.access ?? {};
  const userIdList = userIds.map(({ userId }) => userId);

  return [...positionIds, ...userIdList];
};
